import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/adminApi";
import { Plus, Pencil, Trash2, Save, X, Image as ImageIcon } from "lucide-react";

const emptyTrending = {
  name: "",
  agency: "",
  price_from: "",
  badge_label: "Coming Soon",
  category: "Upcoming",
  image_url: "",
  location: "",
  description: "",
  is_pre_launch: false,
  is_active: true,
  sort_order: 0,
};

export default function TrendingProjectsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const t = await api.get("/admin/trending-projects");
      setItems(t.data);
    } catch {
      toast.error("Failed to load trending projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/trending-projects/${p.id}`);
      toast.success("Trending project deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="font-serif-luxe text-3xl text-[#1D1D1F]">Most Trending Projects</h2>
          <p className="text-sm text-[#616161] mt-2">
            Cards shown in the dark &ldquo;Most Trending Projects in Dubai&rdquo; section on the landing page.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...emptyTrending, sort_order: (items[items.length - 1]?.sort_order || 0) + 1 })}
          className="btn-luxe"
          data-testid="admin-new-trending"
        >
          <Plus size={16} strokeWidth={1.4} /> New Trending Project
        </button>
      </div>

      {loading ? (
        <div className="text-[#616161] py-16 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[#616161] py-16 text-center border border-dashed border-black/15">
          No trending projects yet. Click &ldquo;New Trending Project&rdquo;.
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((p) => (
            <div key={p.id} className="bg-white p-5 flex gap-5 shadow-[0_4px_20px_rgba(0,0,0,0.04)]" data-testid={`admin-trending-row-${p.id}`}>
              <img src={p.image_url} alt={p.location} className="w-24 h-32 object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif-luxe text-xl text-[#1D1D1F]">{p.location}</h3>
                      {!p.is_active && (
                        <span className="text-[0.65rem] tracking-[0.14em] uppercase px-2.5 py-1 bg-red-100 text-red-800">Hidden</span>
                      )}
                    </div>
                    <p className="text-xs text-[#616161] mt-2 max-w-md line-clamp-2">{p.description}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => setEditing(p)}
                      className="w-9 h-9 flex items-center justify-center border border-[#1D1D1F] text-[#1D1D1F] hover:bg-[#1D1D1F] hover:text-white transition-all"
                      title="Edit"
                      data-testid={`admin-trending-edit-${p.id}`}
                    >
                      <Pencil size={14} strokeWidth={1.4} />
                    </button>
                    <button
                      onClick={() => remove(p)}
                      className="w-9 h-9 flex items-center justify-center border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-all"
                      title="Delete"
                      data-testid={`admin-trending-delete-${p.id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.4} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <TrendingEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function TrendingEditor({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...emptyTrending, ...initial });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Name is required by the backend but no longer collected as its own field here,
      // so it's auto-derived from Location to keep the payload valid.
      const payload = {
        ...form,
        name: form.name || form.location || "Untitled Project",
        sort_order: parseInt(form.sort_order, 10) || 0,
      };
      if (isNew) {
        await api.post("/admin/trending-projects", payload);
        toast.success("Trending project created");
      } else {
        await api.put(`/admin/trending-projects/${initial.id}`, payload);
        toast.success("Trending project updated");
      }
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-trending-editor"
    >
      <div className="bg-white w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-black/10">
          <h3 className="font-serif-luxe text-2xl text-[#1D1D1F]">
            {isNew ? "New Trending Project" : "Edit Trending Project"}
          </h3>
          <button onClick={onClose} className="text-[#616161] hover:text-[#1D1D1F]" data-testid="admin-trending-close">
            <X size={20} strokeWidth={1.4} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 grid gap-5" data-testid="admin-trending-form">
          <TField label="Location (e.g. Dubai South)" required>
            <input required className="input-luxe" value={form.location} onChange={update("location")} data-testid="tp-location" />
          </TField>

          <TField label="Description (shown under the card)">
            <textarea
              rows={4}
              className="input-luxe resize-none"
              value={form.description}
              onChange={update("description")}
              data-testid="tp-description"
              placeholder="Short description (3–4 lines)…"
            />
          </TField>

          <div>
            <label className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161] flex items-center gap-2">
              <ImageIcon size={12} strokeWidth={1.4} /> Image URL
            </label>
            <input required type="url" className="input-luxe" placeholder="https://…" value={form.image_url} onChange={update("image_url")} data-testid="tp-image" />
            {form.image_url && (
              <div className="mt-3 flex justify-center bg-[#0A0A0B] p-4 rounded-lg">
                <img src={form.image_url} alt="preview" className="w-72 h-56 object-cover rounded-xl" style={{ filter: "blur(2px) brightness(0.65)" }} />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-6 border-t border-black/10">
            <button type="button" onClick={onClose} className="btn-outline-dark">Cancel</button>
            <button type="submit" disabled={saving} className="btn-luxe" data-testid="admin-trending-save">
              <Save size={14} strokeWidth={1.4} /> {saving ? "Saving…" : "Save Trending Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TField({ label, children }) {
  return (
    <div>
      <label className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161]">{label}</label>
      {children}
    </div>
  );
}